import styled from '@emotion/styled';
import classNames from 'classnames';
import { FC } from 'react';

import { semanticStatusColorLookup, StatusColor } from 'app/components/v2/components/Pill/utils';

import { getSemanticColorVariable } from '../../theme/colors';
import Box, { BoxProps } from '../Box';
import Legal from '../Text/Legal';
import TooltipIcon from '../TooltipIcon';

// Local Typings
interface StyledBoxProps {
  pillSize?: 'medium' | 'small';
  variant?: 'default' | 'outlined';
  verticalAlignLabel?: 'end' | 'start' | null;
}
export interface PillProps extends StyledBoxProps {
  label: string;
  statusColor: StatusColor;
  tooltipLabel?: string;
}

const StyledBox = styled(Box)<StyledBoxProps>(({
  pillSize,
  variant,
  verticalAlignLabel,
}) => ({
  /* eslint-disable @typescript-eslint/naming-convention, sort-keys */
  '--border-width': '2px',
  '--font-size': pillSize === 'small' ? '10px' : '12px',
  '--icon-size': pillSize === 'small' ? '10px' : '16px',
  '--padding-x-base': pillSize === 'small' ? '8px' : '12px',
  '--padding-x': 'calc(var(--padding-x-base) - var(--border-width))',
  '--margin-left-offset': 'calc((var(--padding-x) + var(--border-width)) * -1)',
  '--margin-right-offset': 'calc((var(--padding-x) + var(--border-width)) * -1)',

  backgroundColor: variant === 'outlined' ? 'transparent' : undefined,
  borderWidth: 'var(--border-width)',
  fontSize: 'var(--font-size)',
  marginLeft: verticalAlignLabel === 'start' ? 'var(--margin-left-offset)' : undefined,
  marginRight: verticalAlignLabel === 'end' ? 'var(--margin-right-offset)' : undefined,
  padding: 'calc((var(--font-size) - var(--border-width) * 2) / 2) var(--padding-x)',

  '& > span:has(svg)': {
    margin: -2,
    marginRight: 'calc(var(--padding-x-base) * -0.5)',
  },
  /* eslint-enable @typescript-eslint/naming-convention, sort-keys */
}));

// Component Definition
const Pill: FC<Omit<BoxProps,
  | 'backgroundColor'
  | 'borderRadius'
  | 'children'
  | 'fontColor'
  | 'height'> & PillProps> = ({
  label,
  statusColor = 'blue',
  tooltipLabel,
  ...props
}) => (
  <StyledBox
    {...props}
    alignItems="center"
    as="span"
    backgroundColor={semanticStatusColorLookup[statusColor]}
    border={`2px solid ${getSemanticColorVariable(semanticStatusColorLookup[statusColor])}`}
    borderRadius={20}
    className={classNames(props.className, 'pill')}
    display="flex"
    flexShrink={0}
    gap={2}
    justifyContent="center"
    variant={props.variant}
    verticalAlignLabel={props.verticalAlignLabel}
    width="fit-content"
  >
    <Legal
      as="span"
      fontColor={props.variant === 'outlined' ? 'status_text_outlined' : 'status_text'}
      fontSize="inherit"
      fontWeight="semiBold"
      lineHeight="1.0em"
      noWrap
    >
      {label}
    </Legal>

    {!!tooltipLabel && (
      <TooltipIcon
        iconProps={{
          color: 'status',
          name: 'info',
          size: props.pillSize === 'small' ? 12 : 'xxs',
        }}
        tooltipProps={{
          tooltipLabel,
          tooltipPlacement: 'top',
        }}
      />
    )}
  </StyledBox>
);

export default Pill;
